import React, { useContext, useEffect, useRef } from "react";
import {
  IonAvatar,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonTitle,
  IonToolbar,
  isPlatform,
} from "@ionic/react";
import { call, chatbubbleEllipses, close, mail } from "ionicons/icons";
import { EmployeePhone } from "../models/employees/Employees";
import { useSettingsStore } from "../store/settings";
import { TranslationsContext } from "../util/Translations";
import {
  filterTextablePhones,
  isValidEmail,
  isValidPhone,
  sanitizePhoneForUri,
} from "../util/EmployeeContact";
import useEmployeeContact from "../hooks/useEmployeeContact";

interface EmployeeContactCardProps {
  isOpen: boolean;
  employeeId: string;
  displayName?: string;
  role?: string;
  onClose: () => void;
}

// Open a tel:/sms:/mailto: URI via the OS handler.
const openUri = (uri: string) =>
  window.open(uri, isPlatform("hybrid") ? "_system" : "_blank");

// Initials for the default avatar when the employee has no photo.
const initialsOf = (text: string): string => {
  const t = (text || "").trim();
  if (!t) return "?";
  const parts = t.split(/[\s,]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return t.slice(0, 2).toUpperCase();
};

/**
 * Employee contact card (US 379068). Opened by tapping an employee field on
 * Work Order Details. Shows the employee photo (default initials avatar if
 * none), name/role, and email + phone with one-tap communication actions:
 *   - tap email       -> device email client (valid email only)
 *   - tap call icon   -> native dialer
 *   - tap text icon   -> native SMS (only for SMS-capable phone codes)
 * Call/Text are dedicated icons that act immediately (no intermediate action
 * sheet). Missing/invalid values are gracefully hidden (BR-1, BR-2, BR-4).
 * Contact data is fetched lazily (only while the card is open) and cached.
 */
const EmployeeContactCard: React.FC<EmployeeContactCardProps> = ({
  isOpen,
  employeeId,
  displayName,
  role,
  onClose,
}) => {
  const { translations } = useContext(TranslationsContext);
  const smsCapablePhoneCodes = useSettingsStore(
    (state) => state.smsCapablePhoneCodes
  );
  const { contact, loading } = useEmployeeContact(
    isOpen ? employeeId : undefined
  );

  const name = contact?.name || displayName || employeeId;
  const photo = contact?.photo;
  const email = contact?.email;
  const showEmail = isValidEmail(email);
  const phones = (contact?.phones ?? []).filter((p) => isValidPhone(p.PhoneNumber));
  const textable = new Set(
    filterTextablePhones(phones, smsCapablePhoneCodes).map((p) => p.PhoneNumber)
  );
  // Whether the employee has anything actionable. Used to avoid presenting an
  // empty card (name + avatar only) when there is no email and no phone: once
  // the lazy fetch has settled with nothing usable, show a clear note instead.
  const hasContact = showEmail || phones.length > 0;

  // Size the sheet to its content so a card with a single phone (or just the
  // "no contact" note) doesn't leave the large empty gap a full 3-row card
  // needs. Each contact row maps to a breakpoint stop; the "no contact" note
  // counts as one row. The sheet stays draggable up to 0.9 regardless.
  // Calibrated from the full card (email + 3 phones = 4 rows) sitting snugly at
  // ~0.6; each row is ~0.08 of the viewport, plus a fixed base for the header,
  // avatar, name and subtitle.
  const modalRef = useRef<HTMLIonModalElement>(null);
  const contentRows = hasContact ? (showEmail ? 1 : 0) + phones.length : 1;
  const fitBreakpoint =
    contentRows <= 1
      ? 0.4
      : contentRows === 2
      ? 0.48
      : contentRows === 3
      ? 0.56
      : contentRows === 4
      ? 0.64
      : 0.76;

  // initialBreakpoint is only honoured at presentation time, so once the lazy
  // fetch settles we animate the already-open sheet to the fitted height.
  useEffect(() => {
    if (isOpen && !loading) {
      modalRef.current?.setCurrentBreakpoint(fitBreakpoint);
    }
  }, [isOpen, loading, fitBreakpoint]);

  const subtitleParts = [employeeId];
  const subtitle = subtitleParts;

  const phoneLabel = (p: EmployeePhone) =>
    p.PhoneCode ? `${p.PhoneNumber} (${p.PhoneCode})` : `${p.PhoneNumber}`;

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onClose}
      initialBreakpoint={fitBreakpoint}
      breakpoints={[0, 0.4, 0.48, 0.56, 0.64, 0.76, 0.9]}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translations["lbl_employee"] || "Employee"}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose} aria-label="Close">
              <IonIcon slot="icon-only" icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent class="ion-padding">
        <div className="employee-card-header">
          <IonAvatar className="employee-card-avatar">
            {photo ? (
              <img src={photo} alt={name} />
            ) : (
              <div className="employee-initials">{initialsOf(name)}</div>
            )}
          </IonAvatar>
          <h2>{name}</h2>
          <IonNote>{subtitle}</IonNote>
        </div>

        {!loading && !hasContact ? (
          <IonList>
            <IonItem lines="none">
              <IonLabel className="ion-text-center ion-text-wrap">
                <IonNote>
                  {translations["lbl_no_contact_info"] ||
                    "No contact information available"}
                </IonNote>
              </IonLabel>
            </IonItem>
          </IonList>
        ) : (
          <IonList>
          {showEmail && (
            <IonItem
              button
              detail
              onClick={() => openUri(`mailto:${email}`)}
            >
              <IonIcon slot="start" icon={mail} color="primary" />
              <IonLabel>
                <p>{translations["lbl_email"] || "Email"}</p>
                <span className="contact-value">{email}</span>
              </IonLabel>
            </IonItem>
          )}
          {phones.map((p, i) => (
            <IonItem key={`${p.PhoneCode}-${p.PhoneNumber}-${i}`}>
              <IonIcon slot="start" icon={call} color="primary" />
              <IonLabel>
                <p>{translations["lbl_phone"] || "Phone"}</p>
                <span className="contact-value">{phoneLabel(p)}</span>
              </IonLabel>
              <IonButtons slot="end">
                <IonButton
                  onClick={() =>
                    openUri(`tel:${sanitizePhoneForUri(p.PhoneNumber)}`)
                  }
                  title={translations["lbl_call"] || "Call"}
                  aria-label={translations["lbl_call"] || "Call"}
                >
                  <IonIcon slot="icon-only" icon={call} color="success" />
                </IonButton>
                {textable.has(p.PhoneNumber) && (
                  <IonButton
                    onClick={() =>
                      openUri(`sms:${sanitizePhoneForUri(p.PhoneNumber)}`)
                    }
                    title={translations["lbl_text"] || "Text"}
                    aria-label={translations["lbl_text"] || "Text"}
                  >
                    <IonIcon
                      slot="icon-only"
                      icon={chatbubbleEllipses}
                      color="primary"
                    />
                  </IonButton>
                )}
              </IonButtons>
            </IonItem>
          ))}
          </IonList>
        )}
      </IonContent>
    </IonModal>
  );
};

export default EmployeeContactCard;
